import type { LadderNetwork } from '../model/LadderProgram';
import type { LadderElement, CompareElement, InRangeElement } from '../model/LadderElement';
import type { BooleanExpression } from './expression';
import { exprAnd, exprOr } from './expression';

class UnionFind {
  private parent = new Map<string, string>();

  find(i: string): string {
    if (!this.parent.has(i)) {
      this.parent.set(i, i);
      return i;
    }
    let p = this.parent.get(i) ?? i;
    if (p !== i) {
      p = this.find(p);
      this.parent.set(i, p);
    }
    return p;
  }

  union(i: string, j: string): void {
    const rootI = this.find(i);
    const rootJ = this.find(j);
    if (rootI !== rootJ) {
      this.parent.set(rootI, rootJ);
    }
  }
}

export type CoilInstruction = {
  elementId: string;
  address?: string | undefined;
  type: import('../model/LadderElement').LadderElementType;
  condition: BooleanExpression;
  condition2?: BooleanExpression | undefined;
  value?: string | undefined;
  in1?: string | undefined;
  in2?: string | undefined;
  in?: string | undefined;
  out?: string | undefined;
  label?: string | undefined;
  inMin?: string | undefined;
  inMax?: string | undefined;
  outMin?: string | undefined;
  outMax?: string | undefined;
};

export interface NetworkLogic {
  /** The boolean condition required to energize each coil */
  coils: Array<CoilInstruction>;
  /** The boolean condition indicating if power flows THROUGH the element (for UI power flow) */
  elementConditions: Map<string, BooleanExpression>;
}

export function compileNetworkLogic(network: LadderNetwork): NetworkLogic {
  const uf = new UnionFind();
  const START_NODE = 'START';

  const elements = Object.values(network.elements);

  const getNodeId = (r: number, c: number) => `n_${r}_${c}`;

  // 1. Merge all (r, 0) into START
  // To know the bounds, we find max row
  let maxCol = 0;
  for (const el of elements) {
    if (el.column > maxCol) maxCol = el.column;
    uf.union(getNodeId(el.row, 0), START_NODE);
  }

  // 2. Merge nodes connected by V_LINE
  for (const el of elements) {
    if (el.type === 'V_LINE') {
      uf.union(getNodeId(el.row, el.column), getNodeId(el.row + 1, el.column));
    }
  }

  // 3. Collect directed edges (Contacts, H_LINE)
  interface Edge {
    element: LadderElement;
    fromSuper: string;
    toSuper: string;
  }
  const edges: Edge[] = [];

  for (const el of elements) {
    const isEdge =
      el.type === 'NO_CONTACT' ||
      el.type === 'NC_CONTACT' ||
      el.type === 'P_CONTACT' ||
      el.type === 'N_CONTACT' ||
      el.type === 'NOT_CONTACT' ||
      el.type === 'H_LINE' ||
      el.type === 'IN_RANGE' ||
      el.type.startsWith('CMP_');

    if (isEdge) {
      const fromNode = getNodeId(el.row, el.column);
      const toNode = getNodeId(el.row, el.column + 1);
      edges.push({
        element: el,
        fromSuper: uf.find(fromNode),
        toSuper: uf.find(toNode),
      });
    }
  }

  // 4. Compute Boolean Expression for each super-node
  // Since power only flows left to right, we can compute expressions column by column.
  const nodeExpr = new Map<string, BooleanExpression>();
  nodeExpr.set(uf.find(START_NODE), { type: 'TRUE' });

  // Iterate columns from 1 to maxCol + 1
  for (let c = 1; c <= maxCol + 1; c++) {
    // Find all edges that end in column c
    // Note: A super-node represents nodes in the SAME column.
    // An edge from col A to col B always has B = A + 1.
    // So edges going into column c originate from column c-1.

    // Actually, because of our V_LINE logic, we can just process all edges that target a super-node
    // whose right-most node is in column c. Or even simpler: group edges by `toSuper`, and ensure
    // we process in topological order. Because there are no cycles, we can just resolve iteratively
    // or by column.
    // Let's find all super-nodes that contain at least one node at column c.
    // Wait, an edge's `toSuper` always belongs to column `el.column + 1`.
    const edgesToCol = edges.filter((e) => e.element.column + 1 === c);

    // Group by `toSuper`
    const grouped = new Map<string, Edge[]>();
    for (const e of edgesToCol) {
      const to = e.toSuper;
      const arr = grouped.get(to) ?? [];
      arr.push(e);
      grouped.set(to, arr);
    }

    for (const [toSuper, incomingEdges] of grouped.entries()) {
      let superExpr: BooleanExpression = { type: 'FALSE' };

      for (const edge of incomingEdges) {
        const fromExpr = nodeExpr.get(edge.fromSuper) ?? { type: 'FALSE' };

        let edgeExpr: BooleanExpression;
        if (edge.element.type === 'H_LINE') {
          edgeExpr = { type: 'TRUE' };
        } else if (edge.element.type === 'NOT_CONTACT') {
          edgeExpr = { type: 'NOT', expr: fromExpr, elementId: edge.element.id };
        } else if (edge.element.type === 'P_CONTACT' || edge.element.type === 'N_CONTACT') {
          edgeExpr = {
            type: 'EDGE_CONTACT',
            address: (edge.element as any).address || '',
            edge: edge.element.type === 'P_CONTACT' ? 'RISING' : 'FALLING',
            elementId: edge.element.id,
          };
        } else if (edge.element.type === 'NO_CONTACT' || edge.element.type === 'NC_CONTACT') {
          edgeExpr = {
            type: 'CONTACT',
            address: (edge.element as any).address || '',
            isNormallyClosed: edge.element.type === 'NC_CONTACT',
            elementId: edge.element.id,
          };
        } else if (edge.element.type.startsWith('CMP_')) {
          const cmp = edge.element as CompareElement;
          edgeExpr = {
            type: 'COMPARE',
            op: cmp.type,
            in1: cmp.in1,
            in2: cmp.in2,
            elementId: cmp.id,
          };
        } else if (edge.element.type === 'IN_RANGE') {
          const r = edge.element as InRangeElement;
          edgeExpr = {
            type: 'IN_RANGE',
            in: r.in,
            min: r.min,
            max: r.max,
            elementId: r.id,
          };
        } else {
          edgeExpr = { type: 'FALSE' };
        }

        const pathExpr = edge.element.type === 'NOT_CONTACT' ? edgeExpr : exprAnd(fromExpr, edgeExpr);
        superExpr = exprOr(superExpr, pathExpr);
      }

      // A super node might get paths from different columns? No, all incoming edges to a super node
      // MUST come from the same column, because a super node only merges nodes within the SAME column.
      // So setting it here is safe.
      nodeExpr.set(toSuper, superExpr);
    }
  }

  // 5. Gather coils and element conditions
  const coils: NetworkLogic['coils'] = [];
  const elementConditions = new Map<string, BooleanExpression>();

  for (const el of elements) {
    const superNode = uf.find(getNodeId(el.row, el.column));
    const leftCond = nodeExpr.get(superNode) ?? { type: 'FALSE' };

    const isBlockOrCoil =
      ['COIL', 'SET_COIL', 'RESET_COIL', 'INVERT_COIL', 'TON', 'TOF', 'TP', 'TONR', 'CTU', 'CTD', 'CTUD', 'SR', 'RS', 'SCALE', 'JMP', 'LBL'].includes(el.type) ||
      ['ADD_I', 'SUB_I', 'MUL_I', 'DIV_I', 'ADD_R', 'SUB_R', 'MUL_R', 'DIV_R', 'MOV_W', 'MOV_R'].includes(el.type);

    if (isBlockOrCoil) {
      let cond2: BooleanExpression | undefined;
      if (['CTU', 'CTD', 'CTUD', 'SR', 'RS', 'TONR'].includes(el.type)) {
        const superNode2 = uf.find(getNodeId(el.row + 1, el.column));
        cond2 = nodeExpr.get(superNode2) ?? { type: 'FALSE' };
      }
      
      let val: string | undefined = undefined;
      let in1: string | undefined = undefined;
      let in2: string | undefined = undefined;
      let inVal: string | undefined = undefined;
      let outVal: string | undefined = undefined;
      let addr: string | undefined = undefined;
      let lbl: string | undefined = undefined;
      let inMin: string | undefined = undefined;
      let inMax: string | undefined = undefined;
      let outMin: string | undefined = undefined;
      let outMax: string | undefined = undefined;

      if ('address' in el) addr = (el as any).address;
      if ('presetTime' in el) val = (el as any).presetTime;
      if ('presetValue' in el) val = (el as any).presetValue;
      if ('in1' in el) in1 = (el as any).in1;
      if ('in2' in el) in2 = (el as any).in2;
      if ('in' in el) inVal = (el as any).in;
      if ('out' in el) outVal = (el as any).out;
      if ('label' in el) lbl = (el as any).label;
      if ('inMin' in el) inMin = (el as any).inMin;
      if ('inMax' in el) inMax = (el as any).inMax;
      if ('outMin' in el) outMin = (el as any).outMin;
      if ('outMax' in el) outMax = (el as any).outMax;

      coils.push({
        elementId: el.id,
        address: addr,
        type: el.type,
        condition: leftCond,
        condition2: cond2,
        value: val,
        in1,
        in2,
        in: inVal,
        out: outVal,
        label: lbl,
        inMin,
        inMax,
        outMin,
        outMax,
      });
      elementConditions.set(el.id, leftCond);
    } else if (el.type === 'H_LINE' || el.type === 'V_LINE') {
      elementConditions.set(el.id, leftCond);
    } else if (
      el.type === 'NO_CONTACT' ||
      el.type === 'NC_CONTACT' ||
      el.type === 'P_CONTACT' ||
      el.type === 'N_CONTACT' ||
      el.type === 'NOT_CONTACT' ||
      el.type === 'IN_RANGE' ||
      el.type.startsWith('CMP_')
    ) {
      let edgeExpr: BooleanExpression;
      if (el.type === 'NOT_CONTACT') {
        edgeExpr = { type: 'NOT', expr: leftCond, elementId: el.id };
      } else if (el.type === 'P_CONTACT' || el.type === 'N_CONTACT') {
        edgeExpr = {
          type: 'EDGE_CONTACT',
          address: (el as any).address || '',
          edge: el.type === 'P_CONTACT' ? 'RISING' : 'FALLING',
          elementId: el.id,
        };
      } else if (el.type === 'NO_CONTACT' || el.type === 'NC_CONTACT') {
        edgeExpr = {
          type: 'CONTACT',
          address: (el as any).address || '',
          isNormallyClosed: el.type === 'NC_CONTACT',
          elementId: el.id,
        };
      } else if (el.type.startsWith('CMP_')) {
        const cmp = el as CompareElement;
        edgeExpr = {
          type: 'COMPARE',
          op: cmp.type,
          in1: cmp.in1,
          in2: cmp.in2,
          elementId: cmp.id,
        };
      } else {
        const r = el as InRangeElement;
        edgeExpr = {
          type: 'IN_RANGE',
          in: r.in,
          min: r.min,
          max: r.max,
          elementId: r.id,
        };
      }
      elementConditions.set(el.id, exprAnd(leftCond, edgeExpr));
    }
  }

  return { coils, elementConditions };
}
